import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { IEnrollment } from '@dtos/Enrollment'
import { useToastController } from '@tamagui/toast'
import { useTranslation } from 'react-i18next'
import { usePath, usePathProgressCard } from '@services/api/graphql/path'
import { useLocalSearchParams } from 'expo-router'
import { OperationVariables, QueryResult } from '@apollo/client'

export interface IEnrollmentData extends IEnrollment {}

interface IPathContextProviderProps {
  enrollment?: IEnrollmentData | null
  enrollmentIsLoading: boolean
  error?: Error
  onLoadMore?: () => void
}

export const PathContext = createContext<IPathContextProviderProps>(
  {} as IPathContextProviderProps,
)

interface IUseEnrollmentInterface {
  data: {
    enrollment: IEnrollment
  } | null
  loading: boolean
  error: Error | undefined
  fetchMore: (
    variables: OperationVariables,
  ) => Promise<QueryResult<{ enrollment: IEnrollment }, OperationVariables>>
}

export function PathProvider({ children }: { children: ReactNode }) {
  const [enrollmentData, setEnrollmentData] = useState<IEnrollment | null>(null)
  const [enrollmentIsLoading, setEnrollmentIsLoading] = useState<boolean>(true)
  const [isEmptyPage, setIsEmptyPage] = useState<boolean>(false)
  const toast = useToastController()
  const { t } = useTranslation('toast')
  const { enrollmentId } = useLocalSearchParams()
  const { data, loading, error, fetchMore } = usePath(
    `${enrollmentId}`,
    {
      by: 'last_view_at',
      direction: 'desc',
    },
    {
      offset: 0,
      limit: 10,
    },
  ) as IUseEnrollmentInterface
  const { getProgressCard, error: progressCardError } = usePathProgressCard({
    id: Number(enrollmentId),
    courseIds: null,
    offset: enrollmentData?.schoolProduct.path?.courses?.length || 0,
    limit: 10,
  })

  useEffect(() => {
    if (progressCardError?.graphQLErrors[0].extensions?.code === 'empty_page') {
      setIsEmptyPage(true)
    }
  }, [progressCardError?.graphQLErrors])

  useEffect(() => {
    if (data) {
      setEnrollmentData(data.enrollment)
    }
  }, [data])

  useEffect(() => {
    setEnrollmentIsLoading(loading)
  }, [loading])

  useEffect(() => {
    const fetchProgressCard = async () => {
      if (data) {
        const courseIds = data.enrollment.schoolProduct.path?.courses?.map(
          (course) => course.id,
        )
        if (courseIds) {
          const { data: progressCardData } = await getProgressCard(
            Number(enrollmentId),
            courseIds,
            0,
            courseIds.length,
          )
          if (progressCardData) {
            setEnrollmentData({
              ...data.enrollment,
              progressCard: progressCardData?.enrollment.progressCard,
            })
          }
        }
      }
    }
    fetchProgressCard()
  }, [data, enrollmentId, getProgressCard])

  if (error) {
    toast.show(t('apolloErrors.enrollmentIdsInfo.title'), {
      message: t('genericRequestError.message'),
      type: 'error',
    })
  }

  const fetchMoreProgressCard = useCallback(
    async (ids: number[], offset: number, limit: number) => {
      const { data: newData, error: newError } = await getProgressCard(
        Number(enrollmentId),
        ids,
        offset,
        limit,
      )
      if (newError) {
        setIsEmptyPage(true)
      }
      return newData
    },
    [getProgressCard, enrollmentId],
  )

  const loadMoreCourses = useCallback(async () => {
    if (isEmptyPage) return
    const { data: newData } = await fetchMore({
      variables: {
        offset: enrollmentData?.schoolProduct.path?.courses?.length,
        limit: 10,
      },
    })
    const newCourses = newData?.enrollment.schoolProduct.path?.courses
    const newProgressCard = await fetchMoreProgressCard(
      newCourses?.map((course) => course.id) || [],
      enrollmentData?.schoolProduct.path?.courses?.length || 0,
      newCourses?.length || 0,
    )
    if (newData?.enrollment && newProgressCard?.enrollment && enrollmentData) {
      setEnrollmentData({
        ...enrollmentData,
        schoolProduct: {
          ...enrollmentData.schoolProduct,
          path: {
            id: enrollmentData.schoolProduct.path?.id || 0,
            name: enrollmentData.schoolProduct.path?.name || '',
            description: enrollmentData.schoolProduct.path?.description || '',
            courses: [
              ...(enrollmentData.schoolProduct.path?.courses || []),
              ...(newData.enrollment.schoolProduct.path?.courses || []),
            ],
          },
        },
        progressCard: {
          ...enrollmentData.progressCard,
          courseProgressCards: [
            ...(enrollmentData.progressCard?.courseProgressCards || []),
            ...(newProgressCard.enrollment.progressCard?.courseProgressCards ||
              []),
          ],
        },
      })
    }
  }, [isEmptyPage, fetchMore, enrollmentData, fetchMoreProgressCard])

  if (enrollmentIsLoading) {
    return (
      <PathContext.Provider value={{ enrollmentIsLoading, error }}>
        {children}
      </PathContext.Provider>
    )
  }

  return (
    <PathContext.Provider
      value={{
        enrollment: enrollmentData,
        enrollmentIsLoading,
        error,
        onLoadMore: loadMoreCourses,
      }}
    >
      {children}
    </PathContext.Provider>
  )
}
